import React, { useEffect, useMemo, useState } from 'react';
import { BarChart3, RefreshCw } from 'lucide-react';
import apiService from '../../services/api';
import StockCapital from '../../components/admin/StockCapital';
import { Order } from '../../types';

const monthLabel = (d: Date) => d.toLocaleDateString('es-AR', { month: 'short', year: '2-digit' });

const SkeletonCards: React.FC = () => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 animate-pulse">
    {[1, 2, 3, 4].map((i) => <div key={i} className="h-24 bg-white rounded-xl border border-gray-100 shadow-sm" />)}
  </div>
);

const Statistics: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isRetrying, setIsRetrying] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [usersStats, setUsersStats] = useState<any>(null);
  const [orderStats, setOrderStats] = useState<any>(null);
  const [messageStats, setMessageStats] = useState<any>(null);
  const [productsCount, setProductsCount] = useState(0);
  const [orders, setOrders] = useState<Order[]>([]);

  const fetchStats = async (manualRetry = false) => {
    if (manualRetry) setIsRetrying(true);
    try {
      setLoadError(null);
      const [u, o, m, p, ordRes] = await Promise.all([
        apiService.getUserStats(),
        apiService.getOrderStats(),
        apiService.getContactStats(),
        apiService.getProducts({ includeInactive: true }),
        apiService.getOrders({ limit: 500 })
      ]);
      setUsersStats(u);
      setOrderStats(o);
      setMessageStats(m);
      setProductsCount((p.products || []).length);
      setOrders(ordRes.orders || []);
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.response?.data?.message;
      setLoadError(detail || 'No se pudieron cargar las estadísticas.');
    } finally {
      setIsLoading(false);
      setIsRetrying(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const monthlyData = useMemo(() => {
    const map = new Map<string, { month: string; orders: number; revenue: number }>();
    orders.forEach((o) => {
      const d = new Date(o.createdAt);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      const current = map.get(key) || { month: monthLabel(d), orders: 0, revenue: 0 };
      current.orders += 1;
      current.revenue += o.totalAmountARS || 0;
      map.set(key, current);
    });
    return Array.from(map.values()).slice(-6);
  }, [orders]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Estadísticas</h1>
        <p className="text-gray-600">Análisis de los últimos 30 días</p>
      </div>

      {loadError && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700 flex items-center justify-between gap-4">
          <span>{loadError}</span>
          <button
            onClick={() => fetchStats(true)}
            disabled={isRetrying}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded border border-red-300 hover:bg-red-100 disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${isRetrying ? 'animate-spin' : ''}`} />
            Reintentar
          </button>
        </div>
      )}

      {isLoading ? (
        <SkeletonCards />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
          <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
            <p className="text-sm text-gray-600">Usuarios totales</p>
            <p className="text-2xl font-bold">{usersStats?.total || 0}</p>
            {(usersStats?.total || 0) === 0 && <p className="text-xs text-gray-500 mt-1">Sin usuarios registrados.</p>}
          </div>
          <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
            <p className="text-sm text-gray-600">Productos</p>
            <p className="text-2xl font-bold">{productsCount}</p>
            {productsCount === 0 && <p className="text-xs text-gray-500 mt-1">No hay productos cargados.</p>}
          </div>
          <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
            <p className="text-sm text-gray-600">Pedidos</p>
            <p className="text-2xl font-bold">{orderStats?.total || 0}</p>
            {(orderStats?.total || 0) === 0 && <p className="text-xs text-gray-500 mt-1">Sin ventas este período.</p>}
          </div>
          <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
            <p className="text-sm text-gray-600">Mensajes</p>
            <p className="text-2xl font-bold">{messageStats?.total || 0}</p>
            {(messageStats?.total || 0) === 0 && <p className="text-xs text-gray-500 mt-1">Sin consultas recibidas.</p>}
          </div>
        </div>
      )}

      <StockCapital />

      <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Tendencia mensual</h2>
        {monthlyData.length === 0 ? (
          <div className="py-10 text-center">
            <BarChart3 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-800 font-medium">No hay datos suficientes para graficar</p>
            <p className="text-sm text-gray-500 mt-1">Aparecerán al generar pedidos.</p>
          </div>
        ) : (
          <div className="overflow-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 sm:px-4 py-3 text-left text-sm font-medium text-gray-600">Mes</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Pedidos</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Ingresos ARS</th>
                </tr>
              </thead>
              <tbody>
                {monthlyData.map((m, idx) => (
                  <tr key={`${m.month}-${idx}`} className="border-t border-gray-100">
                    <td className="px-4 py-3">{m.month}</td>
                    <td className="px-4 py-3">{m.orders}</td>
                    <td className="px-4 py-3">${m.revenue.toLocaleString('es-AR')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Statistics;

